// ai-sidebar/components/composer.tsx
import { useRef, useState, useCallback, useEffect } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useAssistant } from "../use-assistant";
import { ORB_GRADIENT } from "./orb";
import { CommandMenu, filterCommands, type CommandDef } from "./command-menu";
import { stripEmDashes } from "./markdown";
import { saveInsight } from "@/app/services/reporting/insights-api-service";

export function Composer() {
  const { status, send, stop, messages, brandName, currentPage } = useAssistant();
  const [value, setValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);
  const working = status === "thinking" || status === "streaming";

  // "/" at the start opens the command palette; the text after it is the query.
  const paletteOpen = value.startsWith("/");
  const query = paletteOpen ? value.slice(1) : "";
  const items = paletteOpen ? filterCommands(query) : [];

  useEffect(() => { setActiveIndex(0); }, [query, paletteOpen]);

  const autosize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, []);

  const submit = () => {
    const text = value.trim();
    if (!text || working) return;
    setValue("");
    requestAnimationFrame(autosize);
    void send(text);
  };

  const runCommand = async (cmd: CommandDef) => {
    setValue("");
    requestAnimationFrame(autosize);
    if (cmd.action.type === "prompt") {
      void send(cmd.action.prompt);
      return;
    }
    if (cmd.action.type === "history") {
      toast("Conversation history is coming soon.");
      return;
    }
    // save-conversation
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && m.complete && m.content);
    if (!lastAssistant) { toast("Nothing to save yet."); return; }
    const brand = brandName || (typeof localStorage !== "undefined" ? localStorage.getItem("bm_brand") : null);
    if (!brand) { toast.error("Select a brand first to save insights."); return; }
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    try {
      await saveInsight({
        brand_name: brand,
        module: "assistant",
        title: (lastUser?.content || "Conversation").slice(0, 120),
        subtitle: stripEmDashes(lastAssistant.content).replace(/\s+/g, " ").trim().slice(0, 200),
        saved_from: "Page Assistant",
        raw_data: { conversation: messages.map((m) => ({ role: m.role, content: m.role === "assistant" ? stripEmDashes(m.content) : m.content })), page: currentPage },
      });
      toast.success("Saved conversation to Insights");
    } catch (e: any) {
      toast.error(e?.message || "Could not save to insights.");
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (paletteOpen) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0))); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter") { e.preventDefault(); if (items[activeIndex]) void runCommand(items[activeIndex]); return; }
      if (e.key === "Escape") { e.preventDefault(); setValue(""); requestAnimationFrame(autosize); return; }
      return;
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape" && working) {
      e.preventDefault();
      stop();
    }
  };

  return (
    <div className="relative border-t border-border" style={{ padding: 18 }}>
      {paletteOpen && (
        <CommandMenu
          query={query}
          items={items}
          activeIndex={activeIndex}
          onSelect={(i) => { if (items[i]) void runCommand(items[i]); }}
        />
      )}

      <div
        className="flex flex-col rounded-xl border border-border bg-card"
        style={{ boxShadow: "var(--elevation-sm)" }}
      >
        <div className="flex items-end gap-2 px-2.5 py-2">
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => { setValue(e.target.value); autosize(); }}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder="Ask about this page…"
            className="flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none leading-relaxed"
            style={{ maxHeight: 96 }}
          />
          <button
            type="button"
            onClick={submit}
            disabled={!value.trim() || working || paletteOpen}
            aria-label="Send"
            className="flex items-center justify-center shrink-0"
            style={{ width: 32, height: 32, borderRadius: 8, background: ORB_GRADIENT, opacity: working ? 0.5 : 1 }}
          >
            <Send className="w-4 h-4" style={{ color: "#fff", strokeWidth: 1.85 }} />
          </button>
        </div>
        <div
          className="text-center text-muted-foreground whitespace-nowrap overflow-hidden px-2.5 pb-1.5"
          style={{ fontSize: 10, lineHeight: 1.4 }}
        >
          Upmode AI can make mistakes, so double-check anything important.
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
        {working ? (
          <span><kbd className="font-mono">esc</kbd> to stop generating</span>
        ) : (
          <>
            <span><kbd className="font-mono">⌘↵</kbd> to send</span>
            <span><kbd className="font-mono">/</kbd> for commands</span>
          </>
        )}
      </div>
    </div>
  );
}
